import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ListEditor } from './ListEditor';
import { MediaManager } from './MediaManager';

const Section = ({ title, hint, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base">{title}</CardTitle>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

const Field = ({ label, testId, value, onChange, multiline, placeholder }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {multiline
      ? <Textarea data-testid={testId} rows={3} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      : <Input data-testid={testId} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />}
  </div>
);

export const BasicsCard = ({ gb, set }) => (
  <Section title="Check-in, address & welcome" testId="gb-basics-card">
    <Field label="Welcome message (shown at top)" testId="gb-intro" multiline value={gb.intro} onChange={(v) => set({ intro: v })} placeholder="Welcome! We're so glad you're here…" />
    <div className="grid grid-cols-2 gap-3">
      <Field label="Check-in time" testId="gb-check-in" value={gb.check_in} onChange={(v) => set({ check_in: v })} />
      <Field label="Check-out time" testId="gb-check-out" value={gb.check_out} onChange={(v) => set({ check_out: v })} />
    </div>
    <Field label="Address" testId="gb-address" value={gb.address} onChange={(v) => set({ address: v })} />
    <Field label="Special notes" testId="gb-special-notes" multiline value={gb.special_notes} onChange={(v) => set({ special_notes: v })} />
    <Field label="Parking note" testId="gb-parking-note" multiline value={gb.parking_note} onChange={(v) => set({ parking_note: v })} />
  </Section>
);

export const RulesCard = ({ gb, set }) => (
  <Section title="House Rules" testId="gb-rules-card">
    <ListEditor testId="gb-rule" items={gb.house_rules} onChange={(v) => set({ house_rules: v })} addLabel="Add rule"
      blank={{ rule: '', detail: '' }} fields={[{ key: 'rule', placeholder: 'Rule (e.g. No smoking)' }, { key: 'detail', placeholder: 'Detail (e.g. $300 fee if violated)' }]} />
  </Section>
);

export const EssentialsCard = ({ gb, set }) => (
  <Section title="Amenities & How-Tos" hint="Thermostat, TV, coffee maker, trash day – anything guests ask about." testId="gb-essentials-card">
    <ListEditor testId="gb-essential" items={gb.essentials} onChange={(v) => set({ essentials: v })} addLabel="Add item"
      blank={{ title: '', description: '' }} fields={[{ key: 'title', placeholder: 'Title (e.g. Thermostat)' }, { key: 'description', placeholder: 'How it works / where it is', multiline: true }]} />
  </Section>
);

export const CheckoutCard = ({ gb, set }) => (
  <Section title="Checkout Checklist" testId="gb-checkout-card">
    <ListEditor testId="gb-checkout" items={gb.checkout_steps} onChange={(v) => set({ checkout_steps: v })} addLabel="Add step"
      blank="" fields={[{ key: 'value', placeholder: 'Step (e.g. Start the dishwasher)' }]} />
  </Section>
);

export const ContactsCard = ({ gb, set }) => (
  <Section title="Contact Your Host" testId="gb-contacts-card">
    <ListEditor testId="gb-contact" items={gb.contacts} onChange={(v) => set({ contacts: v })} addLabel="Add contact"
      blank={{ type: 'phone', value: '', label: '' }}
      fields={[{ key: 'type', placeholder: 'phone or email' }, { key: 'value', placeholder: '+1 (555) 000-0000 or email' }, { key: 'label', placeholder: 'Label (Primary…)' }]} />
  </Section>
);

export const HouseMapCard = ({ gb, set, propertyId }) => (
  <Section title="House Map / Where Things Are" hint="Upload a floor plan, photos of the breaker box, thermostat, trash bins… or a short walkthrough video." testId="gb-house-map-card">
    <Field label="Notes" testId="gb-house-map-notes" multiline value={gb.house_map.notes} onChange={(v) => set({ house_map: { ...gb.house_map, notes: v } })} placeholder="Extra towels are in the hallway closet. Breaker box is in the basement…" />
    <MediaManager testId="gb-house-map-media" propertyId={propertyId} items={gb.house_map.media}
      onChange={(v) => set((prev) => ({ house_map: { ...prev.house_map, media: typeof v === 'function' ? v(prev.house_map.media) : v } }))} />
  </Section>
);

export const GalleryCard = ({ gb, set, propertyId }) => (
  <Section title="Photo & Video Gallery" hint="Photos of the property guests will see in the guidebook." testId="gb-gallery-card">
    <MediaManager testId="gb-gallery-media" propertyId={propertyId} items={gb.gallery}
      onChange={(v) => set((prev) => ({ gallery: typeof v === 'function' ? v(prev.gallery) : v }))} />
  </Section>
);

export const CustomSectionsCard = ({ gb, set, propertyId }) => {
  const update = (i, patch) => set((prev) => ({ custom_sections: prev.custom_sections.map((c, idx) => (idx === i ? { ...c, ...(typeof patch === 'function' ? patch(c) : patch) } : c)) }));
  const add = () => {
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    set((prev) => ({ custom_sections: [...prev.custom_sections, { id, title: 'New section', body: '', media: [] }], section_order: [...prev.section_order, `custom-${id}`] }));
  };
  const remove = (i) => set((prev) => {
    const c = prev.custom_sections[i];
    return { custom_sections: prev.custom_sections.filter((_, idx) => idx !== i), section_order: prev.section_order.filter((k) => k !== `custom-${c.id}`), hidden_sections: prev.hidden_sections.filter((k) => k !== `custom-${c.id}`) };
  });

  return (
    <Section title="Custom sections" hint="Anything else: pool rules, garage code instructions, local tips…" testId="gb-custom-card">
      {gb.custom_sections.map((c, i) => (
        <div key={c.id} data-testid={`gb-custom-${i}`} className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50">
          <div className="flex gap-2">
            <Input data-testid={`gb-custom-title-${i}`} value={c.title} placeholder="Section title" onChange={(e) => update(i, { title: e.target.value })} />
            <Button type="button" data-testid={`gb-custom-remove-${i}`} variant="outline" size="icon" onClick={() => remove(i)} aria-label="Remove section"><Trash2 className="h-4 w-4 text-red-600" /></Button>
          </div>
          <Textarea data-testid={`gb-custom-body-${i}`} rows={4} value={c.body} placeholder="Write anything guests should know…" onChange={(e) => update(i, { body: e.target.value })} />
          <MediaManager testId={`gb-custom-media-${i}`} propertyId={propertyId} items={c.media}
            onChange={(v) => update(i, (cur) => ({ media: typeof v === 'function' ? v(cur.media) : v }))} />
        </div>
      ))}
      <Button type="button" data-testid="gb-custom-add" variant="outline" size="sm" onClick={add}><Plus className="h-4 w-4 mr-1" />Add custom section</Button>
    </Section>
  );
};
